import { Component, Input, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { NoteDataService } from 'src/app/core/services/note-data.service';
import { CommonDataService } from '@sb-tools/typescript/services/common-data.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { SignalRService } from 'src/app/core/services/signalR.service';
import { Subject } from 'rxjs';
import { ReplyTemplateManagerService } from '@sb-tools/typescript/services/reply-template-manager.service';
import { NoteService } from 'src/app/core/services/note.service';
import { TicketRightPanelService } from '../../../core/services/ticket-right-panel.service';
import { ReplyTemplatesModalComponent } from 'src/sb-modules/Frontend-Reply-Templates-Module/reply-templates-modal.component';
import { NewLinePipe } from '@sb-tools/typescript/pipes/new-line.pipe';
import Quill from 'quill';
import { Range } from 'quill/core/selection';
import { WindowRef } from '@sb-tools/typescript/services/window-ref.service';
import { DetailedTicketDTO } from '../../../core/models/dto/detailed-ticket.dto';
import { NoteDeletedEventDTO } from '../../../core/models/dto/signalR/note-deleted-event.dto';
import { NoteEventDTO } from '../../../core/models/dto/signalR/note-event.dto';
import { NoteDTO } from '../../../core/models/dto/note.dto';
import { NoteDeletedResponseDTO } from '../../../core/models/dto/note-deleted-response.dto';
import { NgClass } from '@angular/common';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { LoaderComponent } from '../../../../sb-tools/typescript/components/loader/loader.component';
import { TranslateModule } from '@ngx-translate/core';
import { NoteItemComponent } from './note-item/note-item.component';
import { NewNoteComponent } from '../../../shared/components/new-note/new-note.component';
import { ReversePagingPipe } from '../../../shared/pipes/reverse-paging.pipe';

const ACTION_NOTE_SORTING_TYPE_KEY = 'actionLNoteSortingTypeKey';

@Component({
    selector: 'app-ticket-notes',
    templateUrl: './ticket-notes.component.html',
    styleUrls: ['./ticket-notes.component.scss'],
    imports: [
        NgClass,
        TooltipModule,
        LoaderComponent,
        TranslateModule,
        NoteItemComponent,
        NewNoteComponent,
        ReversePagingPipe,
    ],
})
export class TicketNotesComponent implements OnInit, OnDestroy {
    @ViewChild('.note-items-container', { read: ViewContainerRef }) container: ViewContainerRef;

    @Input() isForPrinting: boolean;
    @Input() ticket: DetailedTicketDTO;

    isAscSort: boolean;
    isFocusNeeded: boolean;
    isLoading = true;
    isShowAllNotes = false;

    notes: NoteDTO[] = [];
    count = 10;
    totalCount = 10;
    quillEditor: Quill;
    quillRange: Range;

    private unsubscriber = new Subject<void>();

    constructor(
        private bsModalService: BsModalService,
        private signalrService: SignalRService,
        private newLinePipe: NewLinePipe,
        private ticketRightPanelService: TicketRightPanelService,
        public replyTemplateManagerService: ReplyTemplateManagerService,
        public commonDataService: CommonDataService,
        public noteDataService: NoteDataService,
        public noteService: NoteService,
        private windowRef: WindowRef,
    ) {
        this.scrollIntoNoteView = this.scrollIntoNoteView.bind(this);
    }

    ngOnInit(): void {
        if (this.isForPrinting) {
            this.isShowAllNotes = true;
        }

        this.isAscSort = this.windowRef.nativeWindow.localStorage.getItem(ACTION_NOTE_SORTING_TYPE_KEY)
            ? this.windowRef.nativeWindow.localStorage.getItem(ACTION_NOTE_SORTING_TYPE_KEY) === 'true'
            : false;

        this.isFocusNeeded = this.ticketRightPanelService.data.isFocusNeeded;

        this.loadNotes();

        this.noteService.newNoteSubject.pipe(takeUntil(this.unsubscriber)).subscribe((newNote) => {
            this.addNote(this.notes, newNote, true);
        });

        this.noteService.scrollToViewNote.pipe(takeUntil(this.unsubscriber)).subscribe(() => {
            this.scrollIntoNoteView();
        });

        this.setupSignalRListeners();

        this.noteService.disableNoteHighlighting = true;
    }

    addNote(notesList: NoteDTO[], newNote: NoteDTO, isWithScrollToView = false): void {
        if (
            !notesList.find((note) => {
                return note.id === newNote.id;
            })
        ) {
            notesList.push(newNote);

            if (isWithScrollToView) {
                setTimeout(() => {
                    const element = document.getElementById('note-container');
                    if (element) {
                        element.scrollTo({ top: this.isAscSort ? element.scrollHeight : 0, behavior: 'smooth' });
                    }
                }, 100);
            }

            this.notes = Object.assign([], this.notes.sort(this.comparesNotes()));
            this.ticket.notes = this.notes;

            setTimeout(() => {
                this.readAllNotes();
            }, 500);
        }
    }

    comparesNotes(): (firstNote: NoteDTO, secondNote: NoteDTO) => 0 | 1 | -1 {
        return (firstNote: NoteDTO, secondNote: NoteDTO): 0 | 1 | -1 => {
            const firstNoteDate: Date = firstNote.creationTime;
            const secondNoteDate: Date = secondNote.creationTime;

            if (firstNoteDate < secondNoteDate) {
                return this.isAscSort ? -1 : 1;
            } else if (firstNoteDate > secondNoteDate) {
                return this.isAscSort ? 1 : -1;
            } else {
                return 0;
            }
        };
    }

    loadNotes(): void {
        if (this.isForPrinting) {
            this.ticket.notes = Object.assign([], this.ticket.notes.sort(this.comparesNotes()));
            this.notes = this.ticket.notes;
            this.isLoading = false;
        } else {
            this.isLoading = true;

            this.noteDataService
                .getAll(this.ticket.id)
                .pipe(
                    finalize(() => {
                        this.isLoading = false;
                    }),
                    takeUntil(this.unsubscriber),
                )
                .subscribe((response: NoteDTO[]) => {
                    response = Object.assign([], response.sort(this.comparesNotes()));

                    this.notes = response;
                    this.ticket.notes = this.notes;

                    if (this.noteService.noteIdWithMentionedUser) {
                        setTimeout(() => {
                            this.scrollIntoNoteView();
                        }, 100);
                    }
                });
        }
    }

    openReplyTemplatesModal($event, quillEditor, existedAttachments): void {
        this.updateQuillSelection(quillEditor);

        const initialState = {
            isTextContent: false,
            confirmCallback: (templateText, attachments) => {
                if (attachments?.length) {
                    existedAttachments.push(...attachments);
                }
                return this.replyTemplateManagerService.chooseReplyTemplate({
                    $event,
                    templateText: this.newLinePipe.transform(templateText),
                    quillEditor,
                    quillRange: this.quillRange,
                });
            },
        };

        this.bsModalService.show(ReplyTemplatesModalComponent, {
            initialState,
        });
    }

    processAddedNote(addedNote: NoteDTO): void {
        if (addedNote.parentNoteId) {
            const parentNote = this.notes.find((note) => {
                return note.id === addedNote.parentNoteId;
            });
            if (parentNote) {
                this.addNote(parentNote.subNotes, addedNote);
            }
        } else {
            this.addNote(this.notes, addedNote);
        }
    }

    processDeletedNote(noteDeletedResponse: NoteDeletedResponseDTO): void {
        const deletedNote = noteDeletedResponse.note;
        if (deletedNote.parentNoteId) {
            if (noteDeletedResponse.isParentHidden) {
                this.notes = this.notes.filter((note) => {
                    return note.id !== deletedNote.parentNoteId;
                });
            } else {
                // todo: check logic
                const parentNote = this.notes.find((note) => {
                    return note.id === deletedNote.parentNoteId;
                });
                parentNote.subNotes = parentNote.subNotes.filter((subNote) => {
                    return subNote.id !== deletedNote.id;
                });
            }
        } else {
            // todo: check logic
            const note = this.notes.find((existedNote) => {
                return existedNote.id === deletedNote.id;
            });
            if (note.subNotes.length) {
                note.isDeleted = true;
            } else {
                this.notes = this.notes.filter((existedNote) => {
                    return existedNote.id !== deletedNote.id;
                });
            }
        }

        this.ticket.notes = this.notes;
    }

    processUpdatedNote(updatedNote: NoteDTO): void {
        if (updatedNote.parentNoteId) {
            const parentNote = this.notes.find((note) => {
                return note.id === updatedNote.parentNoteId;
            });
            if (parentNote) {
                this.updateNote(parentNote.subNotes, updatedNote);
            }
        } else {
            this.updateNote(this.notes, updatedNote);
        }
    }

    scrollIntoNoteView(): void {
        setTimeout(() => {
            const noteToView = document.getElementsByClassName('highlighted');

            if (noteToView.length > 0) {
                noteToView[0].scrollIntoView();
            } else if (
                this.notes.find((note) => {
                    return note.id === this.noteService.noteIdWithMentionedUser;
                })
            ) {
                this.isShowAllNotes = true;
                this.scrollIntoNoteView();
            }
        }, 300);
    }

    onClickSortNotes(): void {
        this.isAscSort = !this.isAscSort;
        this.windowRef.nativeWindow.localStorage.setItem(ACTION_NOTE_SORTING_TYPE_KEY, `${this.isAscSort}`);
        this.notes = Object.assign([], this.notes.sort(this.comparesNotes()));
    }

    updateNote(notesList: NoteDTO[], updatedNote: NoteDTO): void {
        const oldNote = notesList.find((note) => {
            return note.id === updatedNote.id;
        });

        if (oldNote) {
            updatedNote.subNotes = oldNote.subNotes;
            Object.assign(oldNote, updatedNote);
        }

        this.ticket.notes = this.notes;
    }

    updateQuillSelection(quill): void {
        this.quillRange = this.replyTemplateManagerService.updateRange(quill);
    }

    private readAllNotes(): void {
        if (this.ticket.unreadNoteCount) {
            this.noteDataService.readAll(this.ticket.id).pipe(take(1)).subscribe();
        }
    }

    private setupSignalRListeners(): void {
        this.signalrService
            .onNoteAdded()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data: NoteEventDTO) => {
                if (data.ticketId === this.ticket.id) {
                    this.processAddedNote(data.note);
                }
            });

        this.signalrService
            .onNoteUpdated()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data: NoteEventDTO) => {
                if (data.ticketId === this.ticket.id) {
                    this.processUpdatedNote(data.note);
                }
            });

        this.signalrService
            .onNoteDeleted()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data: NoteDeletedEventDTO) => {
                if (data.ticketId === this.ticket.id) {
                    this.processDeletedNote({
                        note: data.note,
                        isParentHidden: data.isParentHidden,
                    } as NoteDeletedResponseDTO);
                }
            });
    }

    ngOnDestroy(): void {
        this.noteService.ignoreParamNoteId = true;
        if (this.noteService.disableNoteHighlighting && this.noteService.noteIdWithMentionedUser) {
            this.noteService.noteIdWithMentionedUser = null;
        }

        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
